// src/Components/Shop/Shop.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { useCategories } from '../../Contexts/CategoryContext';
import { useCompany } from '../../Contexts/CompanyContext';
import { useBrands } from '../../Contexts/BrandContext';
import {
    Product,
    getProductDisplayInfo,
    groupProductsForListing,
    ProductFilters,
    SortOption,
    ConditionFilter
} from '../../Services/productService';
import { Brand } from '../../Services/brandService';
import { useFilteredProductsInfinite, useAvailableTags } from '../../Hooks/useProductQuery';
import { useCarModelsByBrand } from '../../Hooks/useCarModelQuery';
import ProductImage from '../shared/ProductImage';

// ─── Max visible category tabs (excludes "All Cars" and "View All") ───────
const MAX_CATEGORY_TABS = 10;

// ─── Helper: open the SideDrawer from anywhere via a custom DOM event ─────────
const openSideDrawer = () => window.dispatchEvent(new Event('open-side-drawer'));

/* ================= IMAGE WITH PLACEHOLDER ================= */
const ProductImageWithPlaceholder = ProductImage;

/* ================= PRODUCT CARD ================= */
interface ProductCardProps {
    product: Product;
    formatPrice: (price: number) => string;
    displayPrices: boolean;
    onProductClick: (id: string, count?: number) => void;
    brands: Brand[];
    isVisible: boolean;
    batchIndex: number;
    // >1 when this card represents several interchangeable brand-new units
    // (same brand+model+trim+color) collapsed into one listing — see
    // groupProductsForListing in productService.ts.
    count?: number;
}

const ProductCard: React.FC<ProductCardProps> = ({
    product, formatPrice, displayPrices, onProductClick, brands, isVisible, batchIndex, count = 1,
}) => {
    const displayInfo = getProductDisplayInfo(product);
    const isSoldOut   = displayInfo.isOutOfStock;

    const brandName = product.brandId
        ? brands.find(b => b.id === product.brandId)?.name ?? null
        : null;

    const imageContainerStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        paddingBottom: '125%',
        overflow: 'hidden',
        backgroundColor: '#f8f9fa'
    };

    return (
        <div
            className={`col-sm-6 col-md-4 col-lg-3 p-b-35 isotope-item product-card-enter ${isVisible ? 'product-card-visible' : ''}`}
            style={!isVisible ? { opacity: 0 } : batchIndex > 0 ? { animationDelay: `${batchIndex * 100}ms` } : undefined}
        >
            <div className="block2">
                <div className="block2-pic hov-img0" style={imageContainerStyle}>
                    <ProductImageWithPlaceholder src={product.images?.[0]} alt={product.name} />

                    <div className="block2-overlay" />

                    {isSoldOut && (
                        <div className="sold-out-badge-corner">SOLD</div>
                    )}

                    {!isSoldOut && count > 1 && (
                        <div className="available-count-badge-corner">{count} AVAILABLE</div>
                    )}

                    <a
                        href="#"
                        className="block2-btn flex-c-m stext-103 cl2 size-102 bg0 bor2 hov-btn1 p-lr-15 trans-04"
                        onClick={(e) => { e.preventDefault(); onProductClick(product.id, count); }}
                    >
                        View Product
                    </a>
                </div>

                <div className="block2-txt flex-w flex-t p-t-14">
                    <div className="block2-txt-child1 flex-col-l" style={{ width: '100%' }}>
                        <a
                            href="#"
                            className="stext-104 cl4 hov-cl1 trans-04 p-b-6"
                            onClick={(e) => { e.preventDefault(); onProductClick(product.id, count); }}
                        >
                            {product.name}
                        </a>

                        <div className="price-wrapper">
                            {!displayPrices ? (
                                <span className="stext-105 cl3">Contact for price</span>
                            ) : displayInfo.hasDiscount ? (
                                <>
                                    <span className="stext-105 cl3 price-discounted">
                                        {formatPrice(displayInfo.discountedPrice)}
                                    </span>
                                    <span className="price-original">
                                        {formatPrice(displayInfo.price)}
                                    </span>
                                    <span className="discount-badge">
                                        -{displayInfo.discount}%
                                    </span>
                                </>
                            ) : (
                                <span className="stext-105 cl3">
                                    {formatPrice(displayInfo.price)}
                                </span>
                            )}
                        </div>

                        {brandName && (
                            <span className="product-card-brand">{brandName}</span>
                        )}

                        {product.trimName && (
                            <span className="stext-107 cl6" style={{ fontSize: '12px', marginTop: '4px' }}>
                                {product.trimName}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ================= SHOP COMPONENT ================= */
const Shop: React.FC = () => {
    const navigate      = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedCategory, setSelectedCategory]       = useState<string>('*');
    const [selectedSubcategory, setSelectedSubcategory] = useState<string>('*');
    const [selectedBrand, setSelectedBrand]             = useState<string>('*');
    const [selectedModel, setSelectedModel]             = useState<string>('*');
    const [selectedTrim, setSelectedTrim]                = useState<string>('*');
    const [showFilter, setShowFilter]                   = useState(false);
    const [showSearch, setShowSearch]                   = useState(false);
    const [searchQuery, setSearchQuery]                 = useState('');
    const [activeSearchQuery, setActiveSearchQuery]     = useState('');
    const [sortBy, setSortBy]                           = useState<SortOption>('newness');
    const [selectedTags, setSelectedTags]               = useState<string[]>([]);
    const [selectedCondition, setSelectedCondition]     = useState<ConditionFilter | '*'>('*');

    const { categories, activeParentCategories, loading: categoriesLoading } = useCategories();
    const { allBrands, loading: brandsLoading }  = useBrands();
    const { formatPrice, displayPrices }          = useCompany();

    // ── Models (dependent on selectedBrand) and their nested Trims ────────────
    const { data: brandModels = [], isLoading: modelsLoading } = useCarModelsByBrand(
        selectedBrand !== '*' ? selectedBrand : undefined
    );
    const activeModel = selectedModel !== '*'
        ? brandModels.find(m => m.id === selectedModel)
        : undefined;
    const availableTrims = activeModel?.trims.filter(t => t.isActive) ?? [];

    const searchPanelRef = useRef<HTMLDivElement>(null);
    const filterPanelRef = useRef<HTMLDivElement>(null);

    const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
    const batchMapRef = useRef<Map<string, number>>(new Map());

    // ── Slice categories to MAX_CATEGORY_TABS ─────────────────────────────────
    const visibleCategories = activeParentCategories.slice(0, MAX_CATEGORY_TABS);
    const hiddenCategories  = activeParentCategories.slice(MAX_CATEGORY_TABS);

    // ── Determine if the selected category is outside the visible 10 ──────────
    // When a category is chosen from the SideDrawer that isn't in the visible
    // tabs, we resolve it and display it as an extra tab before "View All".
    const isSelectedCategoryVisible =
        selectedCategory === '*' ||
        visibleCategories.some(c => c.id === selectedCategory);

    const overflowSelectedCategory =
        !isSelectedCategoryVisible
            ? (
                hiddenCategories.find(c => c.id === selectedCategory) ??
                activeParentCategories.find(c => c.id === selectedCategory) ??
                null
              )
            : null;

    // ── Listen for category selection events dispatched by the SideDrawer ─────
    // The SideDrawer must dispatch:
    //   window.dispatchEvent(new CustomEvent('drawer-category-selected', { detail: { categoryId } }))
    useEffect(() => {
        const handleDrawerCategorySelected = (e: Event) => {
            const customEvent = e as CustomEvent<{ categoryId: string }>;
            const categoryId = customEvent.detail?.categoryId;
            if (categoryId) {
                // Push the selected category into URL params (clears all other filters)
                const p = new URLSearchParams();
                if (categoryId !== '*') p.set('category', categoryId);
                setSearchParams(p);
            }
        };

        window.addEventListener('drawer-category-selected', handleDrawerCategorySelected);
        return () => {
            window.removeEventListener('drawer-category-selected', handleDrawerCategorySelected);
        };
    }, [setSearchParams]);

    const activeBrandName = selectedBrand !== '*'
        ? allBrands?.find(b => b.id === selectedBrand)?.name
        : null;

    const activeModelName = selectedModel !== '*'
        ? brandModels.find(m => m.id === selectedModel)?.name
        : null;

    const activeTrimName = selectedTrim !== '*'
        ? availableTrims.find(t => t.id === selectedTrim)?.name
        : null;

    const activeSubcategoryName = selectedSubcategory !== '*'
        ? categories?.find(c => c.id === selectedSubcategory)?.name ?? null
        : null;

    const filters: ProductFilters = {
        categoryId:    searchParams.get('category')    || undefined,
        subcategoryId: searchParams.get('subcategory') || undefined,
        brandId:       searchParams.get('brand')       || undefined,
        modelId:       searchParams.get('model')       || undefined,
        trimId:        searchParams.get('trim')        || undefined,
        searchQuery:   searchParams.get('search')      || undefined,
        tags:          searchParams.get('tags')?.split(',').filter(Boolean) || undefined,
        sortBy:        (searchParams.get('sort') as SortOption) || 'newness',
        condition:     (searchParams.get('condition') as ConditionFilter) || undefined,
    };

    const {
        data: productsData,
        isLoading: productsLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isError: productsError,
    } = useFilteredProductsInfinite(filters);

    const { data: availableTags = [] } = useAvailableTags();

    const products = productsData?.pages.flatMap(page => page.products) ?? [];
    const filterKey = JSON.stringify(filters);

    // Collapse identical brand-new units (same brand+model+trim+color) into a
    // single card with a count — pre-owned units are never grouped.
    const listingItems = React.useMemo(() => groupProductsForListing(products), [products]);

    useEffect(() => {
        if (products.length === 0) return;

        setVisibleIds(prev => {
            const isFilterChange = products.every(p => !prev.has(p.id));
            const base = isFilterChange ? new Set<string>() : new Set(prev);

            const newBatchMap = new Map<string, number>();
            let batchIndex = 0;

            for (const product of products) {
                if (!base.has(product.id)) {
                    newBatchMap.set(product.id, batchIndex++);
                    base.add(product.id);
                }
            }

            batchMapRef.current = newBatchMap;

            if (batchIndex === 0 && !isFilterChange) return prev;
            return base;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, filterKey]);

    useEffect(() => {
        const category    = searchParams.get('category')    || '*';
        const subcategory = searchParams.get('subcategory') || '*';
        const brand       = searchParams.get('brand')       || '*';
        const model       = searchParams.get('model')       || '*';
        const trim        = searchParams.get('trim')        || '*';
        const search      = searchParams.get('search')      || '';
        const sort        = (searchParams.get('sort') as SortOption) || 'newness';
        const tags        = searchParams.get('tags')?.split(',').filter(Boolean) || [];
        const condition   = (searchParams.get('condition') as ConditionFilter) || '*';

        setSelectedCategory(category);
        setSelectedSubcategory(subcategory);
        setSelectedBrand(brand);
        setSelectedModel(model);
        setSelectedTrim(trim);
        setActiveSearchQuery(search);
        setSearchQuery(search);
        setSortBy(sort);
        setSelectedTags(tags);
        setSelectedCondition(condition);
    }, [searchParams]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    };

    const handleCategoryClick = (categoryId: string) => {
        const p = new URLSearchParams();
        if (categoryId !== '*') p.set('category', categoryId);
        setSearchParams(p);
    };

    const handleBrandClick = (brandId: string) => {
        const p = new URLSearchParams();
        if (brandId !== '*') p.set('brand', brandId);
        // Changing (or clearing) the brand invalidates whatever model/trim was
        // selected, since trims live inside a model which lives inside a brand.
        setSearchParams(p);
    };

    const handleModelClick = (modelId: string) => {
        const p = new URLSearchParams(searchParams);
        if (modelId !== '*') { p.set('model', modelId); } else { p.delete('model'); }
        p.delete('trim');
        setSearchParams(p);
    };

    const handleTrimClick = (trimId: string) => {
        const p = new URLSearchParams(searchParams);
        if (trimId !== '*') { p.set('trim', trimId); } else { p.delete('trim'); }
        setSearchParams(p);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const p = new URLSearchParams(searchParams);
        if (searchQuery.trim()) { p.set('search', searchQuery.trim()); } else { p.delete('search'); }
        setSearchParams(p);
    };

    const clearSearch = () => {
        const p = new URLSearchParams(searchParams); p.delete('search'); setSearchParams(p);
    };
    const clearBrand = () => {
        const p = new URLSearchParams(searchParams);
        p.delete('brand'); p.delete('model'); p.delete('trim');
        setSearchParams(p);
    };
    const clearModel = () => {
        const p = new URLSearchParams(searchParams); p.delete('model'); p.delete('trim'); setSearchParams(p);
    };
    const clearTrim = () => {
        const p = new URLSearchParams(searchParams); p.delete('trim'); setSearchParams(p);
    };
    const clearSubcategory = () => {
        const p = new URLSearchParams(searchParams); p.delete('subcategory'); setSearchParams(p);
    };

    const toggleTag = (tag: string) => {
        const newTags = selectedTags.includes(tag)
            ? selectedTags.filter(t => t !== tag)
            : [...selectedTags, tag];
        const p = new URLSearchParams(searchParams);
        if (newTags.length > 0) { p.set('tags', newTags.join(',')); } else { p.delete('tags'); }
        setSearchParams(p);
    };

    const handleSortChange = (option: SortOption) => {
        const p = new URLSearchParams(searchParams);
        p.set('sort', option);
        setSearchParams(p);
    };

    const handleConditionClick = (condition: ConditionFilter | '*') => {
        const p = new URLSearchParams(searchParams);
        if (condition === '*') { p.delete('condition'); } else { p.set('condition', condition); }
        setSearchParams(p);
    };

    const handleProductClick = (productId: string, count: number = 1) => {
        navigate(`/product/${productId}`, count > 1 ? { state: { availableCount: count } } : undefined);
    };

    const handleSearchToggle = () => { if (showFilter) setShowFilter(false); setShowSearch(prev => !prev); };
    const handleFilterToggle = () => { if (showSearch) setShowSearch(false); setShowFilter(prev => !prev); };

    // Counts everything the Filter panel controls (brand, subcategory, tags,
    // condition) so the toggle button can show users a filter is applied even
    // when the panel itself is collapsed.
    const activeFilterCount = [
        selectedBrand !== '*',
        selectedModel !== '*',
        selectedTrim !== '*',
        selectedSubcategory !== '*',
        selectedTags.length > 0,
        selectedCondition !== '*',
    ].filter(Boolean).length;

    const isAllActive       = selectedCategory === '*' && selectedSubcategory === '*' && selectedBrand === '*';
    const isInitialLoading  = productsLoading && products.length === 0;

    return (
        <div>
            <Header />

            <section
                className="bg-img1 txt-center p-lr-15 p-tb-92"
                style={{ backgroundImage: `url('asset/images/bg-02.png')` }}
            >
            </section>

            <style>{`
                /* ── Filter toggle: active-filter count badge ── */
                .shop-filter-btn-active {
                    border-color: #717fe0 !important;
                    color: #717fe0 !important;
                }
                .shop-filter-count-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 18px;
                    height: 18px;
                    padding: 0 5px;
                    margin-left: 6px;
                    border-radius: 9px;
                    background: #717fe0;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 700;
                    line-height: 1;
                }

                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-40px); max-height: 0; }
                    to   { opacity: 1; transform: translateY(0);     max-height: 600px; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeInLeft {
                    from { opacity: 0; transform: translateX(-40px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes fadeInScale {
                    from { opacity: 0; transform: scale(0.85); }
                    to   { opacity: 1; transform: scale(1); }
                }
                @keyframes shimmer {
                    0%   { background-position: -1000px 0; }
                    100% { background-position:  1000px 0; }
                }

                /* ── Product card entrance ── */
                @keyframes productFadeInUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .product-card-enter.product-card-visible {
                    animation: productFadeInUp 0.95s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                .panel-enter { animation: slideDown 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .search-panel-wrapper,
                .filter-panel-wrapper { overflow: hidden; width: 100%; }
                .search-input-container { animation: fadeInScale 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .search-input {
                    animation: shimmer 3s infinite;
                    background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%);
                    background-size: 1000px 100%;
                }
                .filter-sort-section { animation: fadeInLeft 0.9s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .filter-tags-section { animation: fadeInLeft 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .filter-tag-item  { animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
                .filter-sort-item { animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
                .filter-tag-item:nth-child(1)  { animation-delay: 0.1s; }
                .filter-tag-item:nth-child(2)  { animation-delay: 0.2s; }
                .filter-tag-item:nth-child(3)  { animation-delay: 0.3s; }
                .filter-tag-item:nth-child(4)  { animation-delay: 0.4s; }
                .filter-tag-item:nth-child(5)  { animation-delay: 0.5s; }
                .filter-tag-item:nth-child(6)  { animation-delay: 0.6s; }
                .filter-tag-item:nth-child(7)  { animation-delay: 0.7s; }
                .filter-tag-item:nth-child(8)  { animation-delay: 0.8s; }
                .filter-tag-item:nth-child(9)  { animation-delay: 0.9s; }
                .filter-tag-item:nth-child(10) { animation-delay: 1.0s; }
                .filter-tag-item:nth-child(n+11) { animation-delay: 1.1s; }
                .filter-sort-item:nth-child(1) { animation-delay: 0.2s; }
                .filter-sort-item:nth-child(2) { animation-delay: 0.4s; }
                .filter-sort-item:nth-child(3) { animation-delay: 0.6s; }
                /* ── Brand/Model/Trim filters use a native <select> instead of a
                   long vertical list — as brands/models/trims grow, a list
                   column just keeps getting taller (especially bad stacked on
                   mobile); a dropdown stays a fixed, compact size no matter
                   how many options it holds. ── */
                .filter-select {
                    width: 100%;
                    max-width: 220px;
                    padding: 9px 32px 9px 12px;
                    font-size: 16px;
                    font-family: inherit;
                    color: #333;
                    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23666' stroke-width='1.5'/%3E%3C/svg%3E") no-repeat right 12px center;
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    cursor: pointer;
                }
                .filter-select:focus { outline: none; border-color: #717fe0; }
                .icon-rotate { transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
                .icon-rotate.active { transform: rotate(180deg); }
                .filter-tag-item { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
                .filter-tag-item:hover { transform: translateY(-3px); box-shadow: 0 6px 12px rgba(0,0,0,0.15); }

                /* ── Sold Out badge ── */
                .sold-out-badge-corner {
                    position: absolute;
                    top: 12px; left: 12px;
                    z-index: 5;
                    background: #e84040;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    padding: 5px 13px;
                    border-radius: 30px;
                    text-transform: uppercase;
                    pointer-events: none;
                    box-shadow: 0 2px 8px rgba(232,64,64,0.35);
                }

                /* ── Grouped "N identical brand-new units" badge ── */
                .available-count-badge-corner {
                    position: absolute;
                    top: 12px; left: 12px;
                    z-index: 5;
                    background: #2f9e44;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    padding: 5px 13px;
                    border-radius: 30px;
                    text-transform: uppercase;
                    pointer-events: none;
                    box-shadow: 0 2px 8px rgba(47,158,68,0.35);
                }

                /* ── Discount styles ── */
                .price-original {
                    text-decoration: line-through;
                    color: #aaa;
                    font-size: 13px;
                    margin-left: 6px;
                    font-weight: 400;
                }
                .price-discounted { color: inherit; font-weight: 600; }
                .price-wrapper {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 2px;
                }
                .discount-badge {
                    display: inline-block;
                    background: #e74c3c;
                    color: #fff;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.8px;
                    padding: 2px 7px;
                    border-radius: 2px;
                    margin-left: 6px;
                    vertical-align: middle;
                    line-height: 1.6;
                }

                /* ── Brand label ── */
                .product-card-brand {
                    display: block;
                    margin-top: 5px;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 1.4px;
                    text-transform: uppercase;
                    color: #717fe0;
                    line-height: 1.4;
                }

                /* ── Active brand pill ── */
                .brand-active-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #333;
                    color: #fff;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 14px;
                    border-radius: 30px;
                    margin-left: 8px;
                    vertical-align: middle;
                }
                .brand-active-pill .pill-clear {
                    background: none; border: none; color: #fff;
                    font-size: 15px; line-height: 1; cursor: pointer;
                    padding: 0; opacity: 0.75; margin-left: 2px;
                }
                .brand-active-pill .pill-clear:hover { opacity: 1; }

                /* ── Active subcategory pill ── */
                .subcategory-active-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #717fe0;
                    color: #fff;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 14px;
                    border-radius: 30px;
                    vertical-align: middle;
                }
                .subcategory-active-pill .pill-clear {
                    background: none; border: none; color: #fff;
                    font-size: 15px; line-height: 1; cursor: pointer;
                    padding: 0; opacity: 0.75; margin-left: 2px;
                }
                .subcategory-active-pill .pill-clear:hover { opacity: 1; }

                /* ── Overflow (drawer-selected) category tab fade-in ── */
                @keyframes shop-overflow-cat-in {
                    from { opacity: 0; transform: translateY(-6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .shop-overflow-cat {
                    animation: shop-overflow-cat-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                /* ── View All button ── */
                .shop-view-all-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    color: #717fe0;
                    background: none;
                    font-size: 12px; 
                    line-height: 1;
                    cursor: pointer;
                    transition: background 0.25s ease, color 0.25s ease, transform 0.2s ease;
                    white-space: nowrap;
                    margin-left: 4px;
                }
                .shop-view-all-btn:hover {
                    transform: translateY(-1px);
                }
                .shop-view-all-btn svg {
                    transition: transform 0.25s ease;
                }
                .shop-view-all-btn:hover svg {
                    transform: translateX(3px);
                }

                /* ── Mobile grid: 2 columns ── */
                @media (max-width: 576px) {
                    .isotope-grid .col-sm-6 {
                        width: 50% !important;
                        padding-left: 8px !important;
                        padding-right: 8px !important;
                        padding-bottom: 20px !important;
                    }
                }
            `}</style>

            <div className="bg0 m-t-23 p-b-140">
                <div className="container">

                    {/* Active search banner */}
                    {activeSearchQuery && (
                        <div className="flex-w flex-sb-m p-b-20">
                            <span className="stext-106 cl6">
                                Search results for: <strong>"{activeSearchQuery}"</strong>
                            </span>
                            <button
                                className="stext-106 cl6 hov1 trans-04"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={clearSearch}
                            >
                                Clear search
                            </button>
                        </div>
                    )}

                    {/* Active brand banner */}
                    {activeBrandName && (
                        <div className="flex-w flex-sb-m p-b-20">
                            <span className="stext-106 cl6">
                                Showing products from: <strong>{activeBrandName}</strong>
                            </span>
                            <button
                                className="stext-106 cl6 hov1 trans-04"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={clearBrand}
                            >
                                Clear brand filter
                            </button>
                        </div>
                    )}

                    {/* Active subcategory banner */}
                    {activeSubcategoryName && (
                        <div className="flex-w flex-sb-m p-b-20">
                            <span className="stext-106 cl6">
                                Showing: <strong>{activeSubcategoryName}</strong>
                            </span>
                            <button
                                className="stext-106 cl6 hov1 trans-04"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={clearSubcategory}
                            >
                                Clear filter
                            </button>
                        </div>
                    )}

                    {/* Error banner */}
                    {productsError && (
                        <div className="flex-w flex-sb-m p-b-20">
                            <span className="stext-106 cl6" style={{ color: '#e84040' }}>
                                Failed to load products. Please try refreshing the page.
                            </span>
                        </div>
                    )}

                    {/* ========== CATEGORY TABS + FILTER + SEARCH ========== */}
                    <div className="flex-w flex-sb-m p-b-52">

                        {/* ── Category tabs (max 10) + overflow selected + pills + View All ── */}
                        <div className="flex-w flex-l-m filter-tope-group m-tb-10" style={{ alignItems: 'center', flexWrap: 'wrap', gap: '0 4px' }}>

                            {/* All Cars */}
                            <button
                                className={`stext-106 cl6 hov1 bor3 trans-04 m-r-32 m-tb-5 ${isAllActive ? 'how-active1' : ''}`}
                                onClick={() => handleCategoryClick('*')}
                            >
                                All Cars
                            </button>

                            {/* Up to MAX_CATEGORY_TABS categories */}
                            {categoriesLoading ? (
                                <span className="stext-106 cl6 m-r-32 m-tb-5">Loading...</span>
                            ) : (
                                visibleCategories.map(category => (
                                    <button
                                        key={category.id}
                                        className={`stext-106 cl6 hov1 bor3 trans-04 m-r-32 m-tb-5 ${selectedCategory === category.id ? 'how-active1' : ''}`}
                                        onClick={() => handleCategoryClick(category.id!)}
                                    >
                                        {category.name}
                                    </button>
                                ))
                            )}

                            {/*
                             * ── Overflow category tab ──────────────────────────────────
                             * Rendered only when the selected category is NOT among the
                             * first MAX_CATEGORY_TABS visible tabs (e.g. chosen via the
                             * SideDrawer). It always appears active (how-active1) and sits
                             * right before the pills and "View All" button, using identical
                             * styles to the regular category tabs.
                             */}
                            {!categoriesLoading && overflowSelectedCategory && (
                                <button
                                    key={overflowSelectedCategory.id}
                                    className="shop-overflow-cat stext-106 cl6 hov1 bor3 trans-04 m-r-32 m-tb-5 how-active1"
                                    onClick={() => handleCategoryClick(overflowSelectedCategory.id!)}
                                >
                                    {overflowSelectedCategory.name}
                                </button>
                            )}

                            {/* Active subcategory pill */}
                            {activeSubcategoryName && (
                                <span className="subcategory-active-pill m-tb-5">
                                    {activeSubcategoryName}
                                    <button className="pill-clear" onClick={clearSubcategory} title="Clear subcategory">×</button>
                                </span>
                            )}

                            {/* Active brand pill */}
                            {activeBrandName && (
                                <span className="brand-active-pill m-tb-5">
                                    {activeBrandName}
                                    <button className="pill-clear" onClick={clearBrand} title="Clear brand">×</button>
                                </span>
                            )}

                            {/* Active model pill */}
                            {activeModelName && (
                                <span className="brand-active-pill m-tb-5">
                                    {activeModelName}
                                    <button className="pill-clear" onClick={clearModel} title="Clear model">×</button>
                                </span>
                            )}

                            {/* Active trim pill */}
                            {activeTrimName && (
                                <span className="brand-active-pill m-tb-5">
                                    {activeTrimName}
                                    <button className="pill-clear" onClick={clearTrim} title="Clear trim">×</button>
                                </span>
                            )}

                            {/* View All — always visible, opens Side Drawer */}
                            {!categoriesLoading && (
                                <button
                                    className="shop-view-all-btn m-tb-5"
                                    onClick={openSideDrawer}
                                    title="Browse all categories and brands"
                                >
                                    View All
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* ── Filter + Search toggles ── */}
                        <div className="flex-w flex-c-m m-tb-10">
                            <button
                                className={`flex-c-m stext-106 cl6 size-104 bor4 pointer hov-btn3 trans-04 m-r-8 m-tb-4 ${activeFilterCount > 0 ? 'shop-filter-btn-active' : ''}`}
                                onClick={handleFilterToggle}
                            >
                                <i className={`cl2 m-r-6 fs-15 trans-04 zmdi icon-rotate ${showFilter ? 'zmdi-close active' : 'zmdi-filter-list'}`}></i>
                                Filter
                                {activeFilterCount > 0 && (
                                    <span className="shop-filter-count-badge">{activeFilterCount}</span>
                                )}
                            </button>
                            <button
                                className="flex-c-m stext-106 cl6 size-105 bor4 pointer hov-btn3 trans-04 m-tb-4"
                                onClick={handleSearchToggle}
                            >
                                <i className={`cl2 m-r-6 fs-15 trans-04 zmdi icon-rotate ${showSearch ? 'zmdi-close active' : 'zmdi-search'}`}></i>
                                Search
                            </button>
                        </div>

                        {/* ── Search panel ── */}
                        {showSearch && (
                            <div ref={searchPanelRef} className="search-panel-wrapper panel-enter">
                                <div className="w-full p-t-10 p-b-15">
                                    <form onSubmit={handleSearchSubmit}>
                                        <div className="bor8 dis-flex p-l-15 search-input-container">
                                            <button type="submit" className="size-113 flex-c-m fs-16 cl2 hov-cl1 trans-04">
                                                <i className="zmdi zmdi-search"></i>
                                            </button>
                                            <input
                                                className="mtext-107 cl2 size-114 plh2 p-r-15 search-input"
                                                type="text"
                                                placeholder="Search products... (Press Enter)"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* ── Filter panel ── */}
                        {showFilter && (
                            <div ref={filterPanelRef} className="filter-panel-wrapper panel-enter">
                                <div className="w-full p-t-10">
                                    <div className="wrap-filter flex-w bg6 w-full p-lr-40 p-t-27 p-b-27 p-lr-15-sm">

                                        <div className="filter-col1 p-r-15 p-b-27 filter-sort-section">
                                            <div className="mtext-102 cl2 p-b-15">Sort By</div>
                                            <ul>
                                                {(['newness', 'price-low', 'price-high'] as SortOption[]).map(option => (
                                                    <li key={option} className="p-b-6 filter-sort-item">
                                                        <button
                                                            onClick={() => handleSortChange(option)}
                                                            className={`filter-link stext-106 trans-04 ${sortBy === option ? 'filter-link-active' : ''}`}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                        >
                                                            {option === 'newness' ? 'Newness' : option === 'price-low' ? 'Price: Low to High' : 'Price: High to Low'}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="filter-col3 p-r-15 p-b-27 filter-sort-section">
                                            <div className="mtext-102 cl2 p-b-15">Condition</div>
                                            <ul>
                                                {([
                                                    { value: '*' as const, label: 'All' },
                                                    { value: 'brand-new' as const, label: 'Brand New' },
                                                    { value: 'pre-owned' as const, label: 'Pre-Owned' },
                                                ]).map(({ value, label }) => (
                                                    <li key={value} className="p-b-6 filter-sort-item">
                                                        <button
                                                            onClick={() => handleConditionClick(value)}
                                                            className={`filter-link stext-106 trans-04 ${selectedCondition === value ? 'filter-link-active' : ''}`}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                        >
                                                            {label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {!brandsLoading && allBrands && allBrands.length > 0 && (
                                            <div className="filter-col2 p-r-15 p-b-27 filter-sort-section">
                                                <div className="mtext-102 cl2 p-b-15">Brand</div>
                                                <select
                                                    className="filter-select"
                                                    value={selectedBrand}
                                                    onChange={(e) => handleBrandClick(e.target.value)}
                                                >
                                                    <option value="*">All Brands</option>
                                                    {allBrands.map(brand => (
                                                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {selectedBrand !== '*' && !modelsLoading && brandModels.length > 0 && (
                                            <div className="filter-col2 p-r-15 p-b-27 filter-sort-section">
                                                <div className="mtext-102 cl2 p-b-15">Model</div>
                                                <select
                                                    className="filter-select"
                                                    value={selectedModel}
                                                    onChange={(e) => handleModelClick(e.target.value)}
                                                >
                                                    <option value="*">All Models</option>
                                                    {brandModels.map(model => (
                                                        <option key={model.id} value={model.id}>{model.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {selectedModel !== '*' && availableTrims.length > 0 && (
                                            <div className="filter-col2 p-r-15 p-b-27 filter-sort-section">
                                                <div className="mtext-102 cl2 p-b-15">Trim</div>
                                                <select
                                                    className="filter-select"
                                                    value={selectedTrim}
                                                    onChange={(e) => handleTrimClick(e.target.value)}
                                                >
                                                    <option value="*">All Trims</option>
                                                    {availableTrims.map(trim => (
                                                        <option key={trim.id} value={trim.id}>{trim.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="filter-col4 p-b-27 filter-tags-section" style={{ flex: 1 }}>
                                            <div className="mtext-102 cl2 p-b-15">Tags</div>
                                            {availableTags.length > 0 ? (
                                                <div className="flex-w p-t-4 m-r--5">
                                                    {availableTags.map(tag => (
                                                        <button
                                                            key={tag}
                                                            onClick={() => toggleTag(tag)}
                                                            className={`flex-c-m stext-107 cl6 size-301 bor7 p-lr-15 trans-04 m-r-5 m-b-5 filter-tag-item ${selectedTags.includes(tag) ? 'hov-tag1' : ''}`}
                                                            style={{
                                                                backgroundColor: selectedTags.includes(tag) ? '#333' : 'transparent',
                                                                color: selectedTags.includes(tag) ? '#fff' : 'inherit',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {tag}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="stext-107 cl6">No tags available</p>
                                            )}
                                            {selectedTags.length > 0 && (
                                                <button
                                                    onClick={() => {
                                                        const p = new URLSearchParams(searchParams);
                                                        p.delete('tags');
                                                        setSearchParams(p);
                                                    }}
                                                    className="stext-106 cl6 hov1 trans-04 m-t-15"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                                >
                                                    Clear all tags
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ========== PRODUCTS GRID ========== */}
                    <div className="row isotope-grid">
                        {listingItems.map(({ product, count }) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                count={count}
                                formatPrice={formatPrice}
                                displayPrices={displayPrices}
                                onProductClick={handleProductClick}
                                brands={allBrands || []}
                                isVisible={visibleIds.has(product.id)}
                                batchIndex={batchMapRef.current.get(product.id) ?? 0}
                            />
                        ))}
                    </div>

                    {isInitialLoading && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5">Loading...</span>
                        </div>
                    )}
                    {isFetchingNextPage && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5">Loading more...</span>
                        </div>
                    )}
                    {!isInitialLoading && !isFetchingNextPage && hasNextPage && products.length > 0 && (
                        <div className="flex-c-m flex-w w-full p-t-45">
                            <button
                                className="flex-c-m stext-101 cl5 size-103 bg2 bor1 hov-btn1 p-lr-15 trans-04"
                                onClick={handleLoadMore}
                                style={{ cursor: 'pointer', border: 'none' }}
                            >
                                Load More
                            </button>
                        </div>
                    )}
                    {!isInitialLoading && !hasNextPage && products.length > 0 && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5">No more products to load</span>
                        </div>
                    )}
                    {!isInitialLoading && products.length === 0 && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5">No cars found matching your search</span>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
            <BackToTop />
        </div>
    );
};

export default Shop;